import { EventEmitter, Injectable } from "@angular/core";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import * as EntityInfoTypeActions from "@app/entity/store/actions/entityinfo-type.actions";
import * as EntityInfoActions from "@app/entity/store/actions/entityinfo.actions";
import * as EntityInfoSelectors from "@app/entity/store/selectors/entityinfo.selectors";
import { RouterActions } from "@app/root-store";
import { CallState, LoadingState } from "@app/root-store/api-result.state";
import { FormatPhonePipe } from "@app/shared/resources/phone-formatter.pipe";
import { CommonModelService } from "@app/shared/services/common-model.service";
import { Action, Store, select } from "@ngrx/store";
import { filter, map, switchMap, take, tap } from "rxjs/operators";
import { entityRoutePaths } from "../entity.routes";
import { EntityInfo, EntityInfoType, EntityInfoTypes } from "../model/entityinfo";
import { EntityState } from "../store/entity.state";

@Injectable({
    providedIn: "root",
})
export class EntityInfoService extends CommonModelService {
    constructor(public store: Store<EntityState>) {
        super(store);
    }

    dispatchGetEntityInfo(entityId: number) {
        this.store.dispatch(
            EntityInfoActions.loadSet.request({
                entityId,
            })
        );
    }

    clearEntityInfoState() {
        this.store.dispatch(EntityInfoActions.clearEntityInfoState());
    }

    selectCentralStations(entityId: number) {
        return this.selectEntityInfoListByType(entityId, EntityInfoTypes.CentralStation);
    }

    selectBuilders(entityId: number) {
        return this.selectEntityInfoListByType(entityId, EntityInfoTypes.HomeBuilders);
    }

    selectCommunities(entityId: number) {
        return this.selectEntityInfoListByType(entityId, EntityInfoTypes.HomeCommunities);
    }

    selectBuildingTypes(entityId: number) {
        return this.selectEntityInfoListByType(entityId, EntityInfoTypes.BuildingType);
    }

    selectCentralStationEmergencyNumbers(entityId: number) {
        return this.selectEntityInfoListByType(entityId, EntityInfoTypes.CentralStationEmergencyNumber);
    }

    selectMunicipalities(entityId: number) {
        return this.selectEntityInfoListByType(entityId, EntityInfoTypes.municipalities);
    }
    selectEntityInfos() {
        return this.store.pipe(select(EntityInfoSelectors.getEntityInfos));
    }

    selectEntityInfo(id: number) {
        return this.store.pipe(select(EntityInfoSelectors.getEntityInfo(id)));
    }

    selectEntityInfoList() {
        return this.store.pipe(select(EntityInfoSelectors.getEntityInfoList));
    }

    entityInfoTypeIsInInitState(entityInfoTypeState: {
        result: {
            [id: number]: EntityInfoType;
        };
        callState: CallState<EntityInfoType>;
    }) {
        if (entityInfoTypeState.callState === LoadingState.init) {
            this.store.dispatch(EntityInfoTypeActions.loadSet.request());
        }
        return entityInfoTypeState.callState === LoadingState.loaded;
    }

    selectEntityInfoListState(entityId: number) {
        return this.store.pipe(
            select(EntityInfoSelectors.selectEntityInfoTypesState),
            filter(entityInfoTypeState => this.entityInfoTypeIsInInitState(entityInfoTypeState)),
            take(1),
            switchMap(() => this.store.pipe(select(EntityInfoSelectors.getEntityInfoCallState))),
            filter(entityInfoCallState => this.dispatchLoadEntityInfo(entityInfoCallState, entityId))
        );
    }

    selectEntityInfoListByType(entityId: number, infoType: EntityInfoTypes) {
        return this.store.pipe(
            select(EntityInfoSelectors.selectEntityInfoTypesState),
            filter(entityInfoTypeState => this.entityInfoTypeIsInInitState(entityInfoTypeState)),
            take(1),
            switchMap(() => this.store.pipe(select(EntityInfoSelectors.getEntityInfoCallState))),
            filter(entityInfoCallState => this.dispatchLoadEntityInfo(entityInfoCallState, entityId)),
            switchMap(() => this.store.pipe(select(EntityInfoSelectors.getEntityInfoListByType(infoType))))
        );
    }

    dispatchLoadEntityInfo(entityInfoCallState: CallState<EntityInfo>, entityId: number) {
        if (entityInfoCallState === LoadingState.init) {
            this.store.dispatch(EntityInfoActions.loadSet.request({ entityId }));
        }
        return entityInfoCallState === LoadingState.loaded;
    }

    selectEntityInfoListByTypeFromStore(infoType: EntityInfoTypes) {
        return this.store.pipe(select(EntityInfoSelectors.getEntityInfoListByType(infoType)));
    }

    selectEntityInfoTypes() {
        return this.store.pipe(
            select(EntityInfoSelectors.selectEntityInfoTypesState),
            filter(entityInfoTypeState => this.entityInfoTypeIsInInitState(entityInfoTypeState)),
            take(1),
            map(state => state.result)
        );
    }

    selectEntityInfoListByTypeId(infoTypeId: number) {
        return this.store.pipe(select(EntityInfoSelectors.getEntityInfoListByTypeId(infoTypeId)));
    }

    getEntityInfoCallState() {
        return this.store.pipe(select(EntityInfoSelectors.getEntityInfoCallState));
    }

    selectEntityInfoById(id) {
        return this.store.pipe(select(EntityInfoSelectors.selectEntityInfoObjectById(id)));
    }

    backToEditEntity(entityId: number) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, "companysettings"],
            })
        );
    }

    goToListEntityInfos(entityId: number, entityInfoSlug: string) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, "companysettings", entityInfoSlug],
            })
        );
    }

    triggerDeleteEntityInfoAction(entityInfo: any) {
        this.store
            .pipe(
                select(EntityInfoSelectors.selectEntityInfoObjectById(entityInfo.id)),
                switchMap(entityinfo =>
                    this.selectEntityInfoTypes().pipe(
                        take(1),
                        tap(infoTypes => {
                            const selectedType = [
                                EntityInfoTypes.HomeBuilders,
                                EntityInfoTypes.HomeCommunities,
                                EntityInfoTypes.BuildingType,
                            ].find(selectedInfoType => infoTypes[entityinfo.entityInfoTypeId].key === selectedInfoType);
                            if (selectedType) {
                                entityinfo[
                                    "warning"
                                ] = `Deleting this ${entityinfo.type?.name} will remove it from all accounts where it is assigned.`;
                            }
                            const logData = this.getDeleteLogData(entityinfo, infoTypes);
                            const modifiedEntityInfo =
                                entityinfo.type?.key === EntityInfoTypes.HomeBuilders
                                    ? { ...entityinfo, type: { ...entityinfo.type, name: "Home Builder" } }
                                    : entityinfo;

                            this.store.dispatch(
                                EntityInfoActions.deleteConfirmationSet.deleteConfirmation({ entityinfo: modifiedEntityInfo, logData })
                            );
                        })
                    )
                )
            )
            .subscribe()
            .unsubscribe();
    }

    triggerDeleteEntityInfoSilentAction(entityInfo: any) {
        this.store
            .pipe(
                select(EntityInfoSelectors.selectEntityInfoObjectById(entityInfo.id)),
                switchMap(entityinfo =>
                    this.selectEntityInfoTypes().pipe(
                        take(1),
                        tap(infoTypes => {
                            const logData = this.getDeleteLogData(entityinfo, infoTypes);
                            this.store.dispatch(EntityInfoActions.deleteSet.request({ entityinfo, logData }));
                        })
                    )
                )
            )
            .subscribe()
            .unsubscribe();
    }

    private getDeleteLogData(
        entityinfo: EntityInfo,
        infoTypes: {
            [id: number]: EntityInfoType;
        }
    ) {
        if (infoTypes[entityinfo.entityInfoTypeId].key === EntityInfoTypes.CentralStationEmergencyNumber) {
            return {
                auditLogName: createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.TWO_WAY_CALLING_NUMBER, LOG_SUB_TYPE.DELETE),
                data: `${entityinfo.data.name}, ${new FormatPhonePipe().transform(entityinfo.data.phone, "x (xxx) xxx-xxxx")}`,
                entityId: entityinfo.entityId,
            };
        }

        return {};
    }

    triggerCreateEntityInfoAction(entityinfo: EntityInfo, callbackEmitter: EventEmitter<Action>, logData: any = null) {
        this.store.dispatch(EntityInfoActions.createSet.request({ entityinfo, callbackEmitter, logData }));
    }

    triggerUpdateEntityInfoAction(entityinfo: EntityInfo, callbackEmitter: EventEmitter<Action>, logData: any = null) {
        this.store.dispatch(EntityInfoActions.updateSet.request({ entityinfo, callbackEmitter, logData }));
    }

    triggerUpdateEntityInfoSuccessAction(action: Action) {
        this.store.dispatch(action);
    }
    triggerCreateEntityInfoSuccessAction(action: Action) {
        this.store.dispatch(action);
    }
}
